import axios from 'axios';

/**
 * HTTP Service for making API calls and handling errors
 * @param {string} url - The request URL
 * @param {Object} options - Configuration for the request (method, headers, body, etc.)
 * @param {function} showSnackbar - Function to show notifications in a Snackbar
 * @param {string} [successMessage] - Optional success message to display on successful response
 * @returns {Promise<any>} - The resolved or rejected HTTP response
 */
export async function httpRequest(url, options, showSnackbar, successMessage) {
    try {
        const response = await axios({
            url,
            params: options.params,
            method: options.method || 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            data: options.body || null,
        });

        // Analyze the response
        if (response.status >= 200 && response.status < 300) {
            // Show success message if provided
            if (successMessage && typeof showSnackbar === 'function') {
                showSnackbar({
                    message: successMessage,
                    severity: 'success',
                });
            }
            return response.data; // Success
        }

        // For unexpected responses
        throw new Error(`Unexpected response status: ${response.status}`);
    } catch (error) {
        let errorMessage = error.response?.data?.message || error.message || 'An error occurred';
        let sev = 'error';
        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            sev = 'warning';
            errorMessage = (
                <>
                    Sesión expirada. Por favor, <a href="/login">inicie sesión</a> nuevamente.
                </>
            );
        }

        // Handle 400 Bad Request
        if (error.response?.status === 400) {
            errorMessage = error.response.data;
        }

        // Check for server error (500)
        if (error.response?.status === 500) {
            errorMessage = 'Error del servidor';
        }

        console.error('HTTP Error:', errorMessage);

        // Trigger a Snackbar for error feedback
        if (typeof showSnackbar === 'function') {
            showSnackbar({
                message: errorMessage,
                severity: sev,
            });
        }

        // Reject the promise to propagate the error to the caller
        return Promise.reject(error);
    }
}
